// Custom stream action: the answer of a structural step of a position grid (a new row, indent,
// move, delete, duplicate, undo …) when only some rows changed — PositionGrid::GridSync. Instead
// of the whole grid (thousands of rows are megabytes of HTML and seconds to render) it carries
// the rows that are new or changed, the ids of the rows that are gone and the new order of all
// of them:
//
//   <turbo-stream action="position_grid_sync" targets="#boq_tree tbody"
//                 order="12,13,14" removed="9" select="13">
//     <template><tr data-grid-row data-row-id="13">…</tr></template>
//   </turbo-stream>
//
// The rows already on screen are moved, not rendered again: moving a <tr> keeps everything the
// controllers hang on it. A row is only moved where it is out of place, so an untouched row keeps
// focus and selection. Afterwards `position-grid:synced` tells the grid's controllers what a
// re-render used to tell them by reconnecting (position-structure: the row to select).
//
// The server diffs against what the rows were just before the step, not against this page: a row
// another user (or another tab) deleted since is not in `removed`, one they added is not among the
// rows sent. `order` is every row there is, so a row on screen that is not in it goes, and a row
// in it that is neither on screen nor sent means the page is behind — it is loaded again, as a
// whole-grid answer would have brought it up to date.
export function syncPositionGrid() {
  const order = ids(this.getAttribute("order"))
  const removed = new Set(ids(this.getAttribute("removed")))
  const incoming = new Map(
    [...this.templateContent.querySelectorAll("tr[data-grid-row]")].map((row) => [
      row.dataset.rowId,
      row,
    ]),
  )

  this.targetElements.forEach((body) => {
    if (!syncRows(body, order, removed, incoming)) return reloadPage()

    body.closest('[data-controller~="position-tree"]')?.dispatchEvent(
      new CustomEvent("position-grid:synced", {
        bubbles: false,
        detail: { select: this.getAttribute("select") || null },
      }),
    )
  })
}

function ids(value) {
  return (value || "").split(",").filter(Boolean)
}

function reloadPage() {
  window.Turbo.visit(window.location.href, { action: "replace" })
}

export function syncRows(body, order, removed, incoming) {
  // A draft row belongs to the step just answered; position-structure forgets it on `synced`.
  body.querySelectorAll("tr[data-position-draft]").forEach((row) => row.remove())

  const rows = new Map(
    [...body.querySelectorAll("tr[data-grid-row]")].map((row) => [row.dataset.rowId, row]),
  )
  const known = new Set(order)
  rows.forEach((row, id) => {
    if (known.has(id) && !removed.has(id)) return

    row.remove()
    rows.delete(id)
  })
  incoming.forEach((row, id) => {
    const fresh = body.ownerDocument.importNode(row, true)
    const current = rows.get(id)
    if (current) current.replaceWith(fresh)
    rows.set(id, fresh)
  })

  // A row of the order the page never had: it is behind (see above), and false says so.
  if (order.some((id) => !rows.has(id))) return false

  // Rows of the order in place, each right after the one before it; the empty-state row stays last.
  let previous = null
  order.forEach((id) => {
    const row = rows.get(id)

    const expected = previous ? previous.nextElementSibling : body.firstElementChild
    if (expected !== row) body.insertBefore(row, expected)
    previous = row
  })
  return true
}
